import {
  customersControllerGetStats,
  requestsControllerFindAll,
  requestsControllerGetDashboardStats,
} from '@kalba/api-client';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import {
  ArrowRight,
  BadgeCheck,
  BarChart3,
  Beaker,
  Briefcase,
  Building2,
  CheckCircle2,
  ClipboardList,
  Clock,
  CreditCard,
  FileCheck2,
  FlaskConical,
  Gavel,
  Microscope,
  Plus,
  Send,
  ShieldAlert,
  TrendingDown,
  TrendingUp,
  UserCheck,
  UserPlus,
  Users,
  Zap,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Label,
  LabelList,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from 'recharts';
import { AppLayout } from '../components/layout/app-layout';
import { ActionCard } from '../components/shared/action-card';
import { StatCard } from '../components/shared/stat-card';
import { Badge } from '../components/ui/badge';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '../components/ui/chart';
import { Skeleton } from '../components/ui/skeleton';
import { usePermissions } from '../hooks/use-permissions';
import { api } from '../lib/ky';
import { cn } from '../lib/utils';
import { useAuthStore } from '../stores/auth.store';

// ── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/dashboard')({
  beforeLoad: () => {
    const { user } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
  },
  component: DashboardPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

type RequestStatus =
  | 'DRAFT'
  | 'SUBMITTED_FOR_INSPECTION'
  | 'INSPECTION_IN_PROGRESS'
  | 'PLEDGE_STUDY_REQUEST'
  | 'APPROVAL_REQUEST'
  | 'APPROVED'
  | 'REJECTED'
  | 'PAYMENT_PROCESS'
  | 'PAID'
  | 'PERMIT_ISSUED'
  | 'SENT_TO_SAP';

interface PermitItem {
  id: string;
  referenceNo: string;
  status: RequestStatus;
  requestTypeConfig?: { name: string; code: string } | null;
  account?: { name: string } | null;
  createdAt: string;
}

interface CustomerStats {
  totalCustomers: { value: number; delta: string };
  activeAccounts: { value: number; delta: string };
  pendingVerification: { value: number; delta: string };
  totalPermits: { value: number; delta: string };
}

interface DashboardStats {
  permitsIssuedMTD: number;
  permitsSubmittedMTD: number;
  rejectionRate: number;
  permitsByMonth: { month: string; count: number }[];
}

type StudyRequestStatus =
  | 'NEW'
  | 'ASSIGNED'
  | 'SITE_VISIT_SCHEDULED'
  | 'REQUEST_LAB_TEST'
  | 'LAB_SITE_VISIT'
  | 'PREPARE_STUDY_REPORT'
  | 'STUDY_REPORT_SM_APPROVAL'
  | 'FINAL_REPORT_ISSUED'
  | 'REJECTED';

interface StudyDashboardStats {
  issuedMTD: number;
  submittedMTD: number;
  rejectionRate: number;
  inPipelineCount: number;
  studyRequestsByMonth: { month: string; count: number }[];
  byStatus: { status: StudyRequestStatus; count: number }[];
}

interface LabDashboardStats {
  unassignedCount: number;
  totalCount: number;
  myInProgressCount: number;
}

interface StudyRequestListItem {
  id: string;
  referenceNo: string;
  status: StudyRequestStatus;
  createdAt: string;
  account: { id: string; name: string };
}

interface StudyRequestsResponse {
  items: StudyRequestListItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS_LABEL: Partial<Record<RequestStatus, string>> = {
  DRAFT: 'Draft',
  SUBMITTED_FOR_INSPECTION: 'For Inspection',
  INSPECTION_IN_PROGRESS: 'In Inspection',
  APPROVAL_REQUEST: 'Awaiting Approval',
  APPROVED: 'Approved',
  PAYMENT_PROCESS: 'Payment',
  PAID: 'Paid',
  PERMIT_ISSUED: 'Issued',
  REJECTED: 'Rejected',
  SENT_TO_SAP: 'In SAP',
  PLEDGE_STUDY_REQUEST: 'Pledge Study',
};

type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'primary';

const STATUS_BADGE: Partial<Record<RequestStatus, BadgeVariant>> = {
  DRAFT: 'default',
  SUBMITTED_FOR_INSPECTION: 'warning',
  INSPECTION_IN_PROGRESS: 'warning',
  APPROVAL_REQUEST: 'primary',
  APPROVED: 'success',
  PAYMENT_PROCESS: 'warning',
  PAID: 'success',
  PERMIT_ISSUED: 'success',
  REJECTED: 'error',
  SENT_TO_SAP: 'primary',
  PLEDGE_STUDY_REQUEST: 'warning',
};

const PERMIT_TYPE_COLOR: Record<string, string> = {
  ENV: 'bg-emerald-50 text-emerald-700',
  WASTE: 'bg-amber-50 text-amber-700',
  VEH: 'bg-blue-50 text-blue-700',
};

const CHART_STATUS_ORDER: RequestStatus[] = [
  'DRAFT',
  'SUBMITTED_FOR_INSPECTION',
  'INSPECTION_IN_PROGRESS',
  'PLEDGE_STUDY_REQUEST',
  'APPROVAL_REQUEST',
  'APPROVED',
  'PAYMENT_PROCESS',
  'PAID',
  'PERMIT_ISSUED',
];

const PIPELINE_STATUS_LABELS = [
  'For Inspection',
  'In Inspection',
  'Pledge Study',
  'Awaiting Approval',
  'Approved',
  'Payment',
  'Paid',
];

const STUDY_STATUS_LABEL: Partial<Record<StudyRequestStatus, string>> = {
  NEW: 'New',
  ASSIGNED: 'Assigned',
  SITE_VISIT_SCHEDULED: 'Site Visit',
  REQUEST_LAB_TEST: 'Lab Test',
  LAB_SITE_VISIT: 'Lab Site Visit',
  PREPARE_STUDY_REPORT: 'Preparing Report',
  STUDY_REPORT_SM_APPROVAL: 'Awaiting Approval',
  FINAL_REPORT_ISSUED: 'Issued',
  REJECTED: 'Rejected',
};

const STUDY_STATUS_BADGE: Partial<Record<StudyRequestStatus, BadgeVariant>> = {
  NEW: 'default',
  ASSIGNED: 'primary',
  SITE_VISIT_SCHEDULED: 'warning',
  REQUEST_LAB_TEST: 'warning',
  LAB_SITE_VISIT: 'warning',
  PREPARE_STUDY_REPORT: 'warning',
  STUDY_REPORT_SM_APPROVAL: 'primary',
  FINAL_REPORT_ISSUED: 'success',
  REJECTED: 'error',
};

const TYPE_COLORS: Record<string, string> = {
  ENV: 'var(--color-chart-1)',
  WASTE: 'var(--color-chart-2)',
  VEH: 'var(--color-chart-3)',
  Other: 'var(--color-chart-4)',
};

const chartConfig = { count: { label: 'Permits', color: 'var(--color-chart-1)' } };
const monthlyChartConfig = { count: { label: 'Submitted', color: 'var(--color-chart-2)' } };
const studyChartConfig = { count: { label: 'Study Requests', color: 'var(--color-chart-1)' } };
const studyMonthlyChartConfig = { count: { label: 'Submitted', color: 'var(--color-chart-2)' } };
const donutChartConfig = {
  ENV: { label: 'Environmental', color: 'var(--color-chart-1)' },
  WASTE: { label: 'Waste', color: 'var(--color-chart-2)' },
  VEH: { label: 'Vehicle', color: 'var(--color-chart-3)' },
  Other: { label: 'Other', color: 'var(--color-chart-4)' },
};

// ── Queries ───────────────────────────────────────────────────────────────────

function usePermitCount(status: RequestStatus | RequestStatus[], enabled: boolean) {
  const key = Array.isArray(status) ? status.join(',') : status;
  return useQuery({
    queryKey: ['permits', 'count', key],
    queryFn: () => {
      const req = Array.isArray(status)
        ? requestsControllerFindAll({ statuses: status.join(','), limit: 1, page: 1 })
        : requestsControllerFindAll({ status, limit: 1, page: 1 });
      return (req as unknown as Promise<{ total: number }>).then((r) => r.total);
    },
    enabled,
    staleTime: 60_000,
  });
}

function usePermitTypeCount(permitTypeCode: string, enabled: boolean) {
  return useQuery({
    queryKey: ['permits', 'count', 'type', permitTypeCode],
    queryFn: () =>
      (
        requestsControllerFindAll({ permitTypeCode, limit: 1, page: 1 }) as unknown as Promise<{
          total: number;
        }>
      ).then((r) => r.total),
    enabled,
    staleTime: 60_000,
  });
}

function useRecentPermits(enabled: boolean) {
  return useQuery({
    queryKey: ['permits', 'recent'],
    queryFn: () =>
      (
        requestsControllerFindAll({ limit: 6, page: 1 }) as unknown as Promise<{
          data: PermitItem[];
          total: number;
        }>
      ).then((r) => r.data),
    enabled,
    staleTime: 30_000,
  });
}

function useStatusBreakdown(enabled: boolean) {
  return useQuery({
    queryKey: ['permits', 'status-breakdown'],
    queryFn: async () => {
      const results = await Promise.all(
        CHART_STATUS_ORDER.map((s) =>
          (
            requestsControllerFindAll({ status: s, limit: 1, page: 1 }) as unknown as Promise<{
              total: number;
            }>
          ).then((r) => ({ status: s, total: r.total })),
        ),
      );
      return results.map((r) => ({
        name: STATUS_LABEL[r.status] ?? r.status,
        count: r.total,
      }));
    },
    enabled,
    staleTime: 60_000,
  });
}

function useStudyRequestCount(key: string, params: Record<string, string>, enabled: boolean) {
  return useQuery({
    queryKey: ['study-requests', 'count', key],
    queryFn: () =>
      api
        .get('study-requests', { searchParams: { ...params, page: '1', limit: '1' } })
        .json<StudyRequestsResponse>()
        .then((r) => r.meta.total),
    enabled,
    staleTime: 60_000,
  });
}

// ── Page ──────────────────────────────────────────────────────────────────────

function DashboardPage() {
  // Sharjah-scoped users (has SHARJAH_MUNICIPALITY, not KALBA_MUNICIPALITY) get a
  // Study Request-only dashboard — no Permit/Kalba Municipality KPIs. Users with both
  // authorities (or an unrestricted role) keep seeing both sections.
  const { can, isSharjahOnly } = usePermissions();
  const user = useAuthStore((s) => s.user);

  const canViewPermits =
    can('permit:view') ||
    can('permit:create') ||
    can('permit:approve') ||
    can('permit:pay') ||
    can('permit:assign-inspection');
  const canViewCustomers = can('customer:view') && !isSharjahOnly;
  const canViewStudyRequests = can('sm-study:view');
  // sm-lab:assign is the permission unique to Lab Technicians (LAB_USER role) —
  // BEEAH_ADMINISTRATOR/BEEAH_CONSULTANT can create/view lab requests but not
  // self-assign them, so this distinguishes "a lab user" as the request specified.
  const isLabUser = can('sm-lab:assign');

  // Stats
  const { data: customerStats, isLoading: statsLoading } = useQuery({
    queryKey: ['customer-stats'],
    queryFn: () => customersControllerGetStats() as unknown as Promise<CustomerStats>,
    enabled: canViewCustomers,
    staleTime: 60_000,
  });

  // Action counts
  const inspectionCount = usePermitCount(
    ['SUBMITTED_FOR_INSPECTION', 'INSPECTION_IN_PROGRESS'],
    can('permit:assign-inspection'),
  );
  const approvalCount = usePermitCount('APPROVAL_REQUEST', can('permit:approve'));
  const pledgeStudyCount = usePermitCount('PLEDGE_STUDY_REQUEST', can('pledge:create'));
  const paidCount = usePermitCount('PAID', can('permit:issue'));
  const draftCount = usePermitCount('DRAFT', can('permit:create'));
  const paymentCount = usePermitCount('PAYMENT_PROCESS', can('permit:pay'));

  // Study Request action counts — "needs your attention" cards
  const needsConsultantCount = useStudyRequestCount(
    'needs-consultant',
    { status: 'NEW' },
    can('sm-study:assign-consultant'),
  );
  const assignedToMeCount = useStudyRequestCount(
    'assigned-to-me',
    {
      assignedConsultantId: user?.id ?? '',
      statuses:
        'ASSIGNED,SITE_VISIT_SCHEDULED,REQUEST_LAB_TEST,LAB_SITE_VISIT,PREPARE_STUDY_REPORT,STUDY_REPORT_SM_APPROVAL',
    },
    can('sm-study-report:create') && !!user?.id,
  );
  const needsAuthorityCount = useStudyRequestCount(
    'needs-authority',
    { status: 'STUDY_REPORT_SM_APPROVAL', unassignedAuthorityOnly: 'true' },
    can('sm-study:assign-authority'),
  );
  const pendingMyApprovalCount = useStudyRequestCount(
    'pending-my-approval',
    { status: 'STUDY_REPORT_SM_APPROVAL', authorityAssigneeId: user?.id ?? '' },
    can('sm-study-report:approve') && !!user?.id,
  );

  // Dashboard stats (MTD, monthly trend)
  const { data: dashboardStats, isLoading: statsLoading2 } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => requestsControllerGetDashboardStats() as unknown as Promise<DashboardStats>,
    enabled: canViewPermits,
    staleTime: 60_000,
  });

  // Permit type breakdown for donut chart
  const envCount = usePermitTypeCount('ENV', canViewPermits);
  const wasteCount = usePermitTypeCount('WASTE', canViewPermits);
  const vehCount = usePermitTypeCount('VEH', canViewPermits);

  // Chart + recent
  const recentQuery = useRecentPermits(canViewPermits);
  const breakdownQuery = useStatusBreakdown(canViewPermits);

  // Study Request dashboard stats (MTD, monthly trend, pipeline breakdown)
  const { data: studyStats, isLoading: studyStatsLoading } = useQuery({
    queryKey: ['study-dashboard-stats'],
    queryFn: () => api.get('study-requests/dashboard-stats').json<StudyDashboardStats>(),
    enabled: canViewStudyRequests,
    staleTime: 60_000,
  });

  const recentStudyQuery = useQuery({
    queryKey: ['study-requests', 'recent'],
    queryFn: () =>
      api
        .get('study-requests', { searchParams: { page: '1', limit: '6' } })
        .json<StudyRequestsResponse>(),
    enabled: canViewStudyRequests,
    staleTime: 30_000,
  });

  const { data: labStats, isLoading: labStatsLoading } = useQuery({
    queryKey: ['lab-dashboard-stats'],
    queryFn: () => api.get('lab-requests/dashboard-stats').json<LabDashboardStats>(),
    enabled: isLabUser,
    staleTime: 60_000,
  });

  const greeting = getGreeting();
  const today = new Date().toLocaleDateString('en-AE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  // In-pipeline count derived from breakdown data (excludes DRAFT, PERMIT_ISSUED)
  const inPipelineCount =
    breakdownQuery.data
      ?.filter((d) => PIPELINE_STATUS_LABELS.includes(d.name))
      .reduce((sum, d) => sum + d.count, 0) ?? 0;

  // Donut chart data
  const typeData = [
    { name: 'ENV', label: 'Environmental', count: envCount.data ?? 0 },
    { name: 'WASTE', label: 'Waste', count: wasteCount.data ?? 0 },
    { name: 'VEH', label: 'Vehicle', count: vehCount.data ?? 0 },
  ].filter((d) => d.count > 0);

  // Monthly trend data
  const monthlyData = (dashboardStats?.permitsByMonth ?? []).map((d) => ({
    month: new Date(`${d.month}-01`).toLocaleDateString('en-AE', {
      month: 'short',
      year: '2-digit',
    }),
    count: d.count,
  }));

  // Study Request pipeline breakdown (bar chart)
  const studyBreakdownData = (studyStats?.byStatus ?? []).map((d) => ({
    name: STUDY_STATUS_LABEL[d.status] ?? d.status,
    count: d.count,
  }));

  // Study Request monthly trend data
  const studyMonthlyData = (studyStats?.studyRequestsByMonth ?? []).map((d) => ({
    month: new Date(`${d.month}-01`).toLocaleDateString('en-AE', {
      month: 'short',
      year: '2-digit',
    }),
    count: d.count,
  }));

  const actionItems = [
    {
      key: 'inspection',
      icon: <Clock size={20} />,
      iconBg: 'bg-warning-50',
      iconColor: 'text-warning-600',
      label: 'Need Inspection',
      value: inspectionCount.data,
      loading: inspectionCount.isLoading,
      description: 'Permits waiting for inspection assignment',
      to: '/permits',
      search: { page: 1, limit: 20, status: 'SUBMITTED_FOR_INSPECTION,INSPECTION_IN_PROGRESS' },
      show: can('permit:assign-inspection'),
    },
    {
      key: 'approval',
      icon: <FileCheck2 size={20} />,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
      label: 'Need Approval',
      value: approvalCount.data,
      loading: approvalCount.isLoading,
      description: 'Permits waiting for section head approval',
      to: '/permits',
      search: { page: 1, limit: 20, status: 'APPROVAL_REQUEST' },
      show: can('permit:approve'),
    },
    {
      key: 'pledge-study',
      icon: <FlaskConical size={20} />,
      iconBg: 'bg-teal-50',
      iconColor: 'text-teal-600',
      label: 'Pledge & Study',
      value: pledgeStudyCount.data,
      loading: pledgeStudyCount.isLoading,
      description: 'Permits awaiting pledge and study',
      to: '/permits',
      search: { page: 1, limit: 20, status: 'PLEDGE_STUDY_REQUEST' },
      show: can('pledge:create'),
    },
    {
      key: 'draft',
      icon: <ClipboardList size={20} />,
      iconBg: 'bg-brand-50',
      iconColor: 'text-brand-500',
      label: 'Draft Permits',
      value: draftCount.data,
      loading: draftCount.isLoading,
      description: 'Permits in draft — not yet submitted',
      to: '/permits',
      search: { page: 1, limit: 20, status: 'DRAFT' },
      show: can('permit:create'),
    },
    {
      key: 'payment',
      icon: <CreditCard size={20} />,
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-600',
      label: 'Pending Payment',
      value: paymentCount.data,
      loading: paymentCount.isLoading,
      description: 'Approved permits awaiting payment',
      to: '/permits',
      search: { page: 1, limit: 20, status: 'PAYMENT_PROCESS' },
      show: can('permit:pay'),
    },
    {
      key: 'ready-to-issue',
      icon: <FileCheck2 size={20} />,
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      label: 'Ready to Issue',
      value: paidCount.data,
      loading: paidCount.isLoading,
      description: 'Payment received — permits ready to issue',
      to: '/permits',
      search: { page: 1, limit: 20, status: 'PAID' },
      show: can('permit:issue'),
    },
    {
      key: 'study-needs-consultant',
      icon: <UserPlus size={20} />,
      iconBg: 'bg-brand-50',
      iconColor: 'text-brand-500',
      label: 'Needs Consultant',
      value: needsConsultantCount.data,
      loading: needsConsultantCount.isLoading,
      description: 'New study requests waiting for a consultant to be assigned',
      to: '/study-requests',
      search: { page: 1, limit: 20, status: 'NEW' },
      show: can('sm-study:assign-consultant'),
    },
    {
      key: 'study-assigned-to-me',
      icon: <Briefcase size={20} />,
      iconBg: 'bg-teal-50',
      iconColor: 'text-teal-600',
      label: 'Assigned to You',
      value: assignedToMeCount.data,
      loading: assignedToMeCount.isLoading,
      description: 'Study requests you are the assigned consultant on',
      to: '/study-requests',
      search: { page: 1, limit: 20, assignedConsultantId: user?.id },
      show: can('sm-study-report:create') && !!user?.id,
    },
    {
      key: 'study-needs-authority',
      icon: <UserCheck size={20} />,
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-600',
      label: 'Needs Authority Assignee',
      value: needsAuthorityCount.data,
      loading: needsAuthorityCount.isLoading,
      description: 'Study reports awaiting SM approval with no reviewer assigned yet',
      to: '/study-requests',
      search: {
        page: 1,
        limit: 20,
        status: 'STUDY_REPORT_SM_APPROVAL',
        unassignedAuthorityOnly: 'true',
      },
      show: can('sm-study:assign-authority'),
    },
    {
      key: 'study-pending-my-approval',
      icon: <Gavel size={20} />,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
      label: 'Pending Your Approval',
      value: pendingMyApprovalCount.data,
      loading: pendingMyApprovalCount.isLoading,
      description: 'Study reports assigned to you for SM approval',
      to: '/study-requests',
      search: { page: 1, limit: 20, authorityAssigneeId: user?.id },
      show: can('sm-study-report:approve') && !!user?.id,
    },
    {
      key: 'lab-unassigned',
      icon: <Beaker size={20} />,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600',
      label: 'Unassigned Lab Requests',
      value: labStats?.unassignedCount,
      loading: labStatsLoading,
      description: 'Lab requests waiting for a lab technician to self-assign',
      to: '/study-requests',
      search: { page: 1, limit: 20 },
      show: isLabUser,
    },
    {
      key: 'lab-total',
      icon: <ClipboardList size={20} />,
      iconBg: 'bg-gray-100',
      iconColor: 'text-gray-600',
      label: 'Total Lab Requests',
      value: labStats?.totalCount,
      loading: labStatsLoading,
      description: 'All lab requests made across every study request',
      to: '/study-requests',
      search: { page: 1, limit: 20 },
      show: isLabUser,
    },
    {
      key: 'lab-my-in-progress',
      icon: <Microscope size={20} />,
      iconBg: 'bg-teal-50',
      iconColor: 'text-teal-600',
      label: 'Your Lab Requests In Progress',
      value: labStats?.myInProgressCount,
      loading: labStatsLoading,
      description: 'Lab requests assigned to you that are still in progress',
      to: '/study-requests',
      search: { page: 1, limit: 20 },
      show: isLabUser,
    },
  ].filter((a) => a.show);

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* ── Welcome banner ─────────────────────────────────────────────── */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand-600 via-brand-500 to-emerald-500 p-6 sm:p-8 min-h-[160px] sm:min-h-[190px] flex flex-col justify-center text-white shadow-lg shadow-brand-500/20">
          <style>{`
            @keyframes pulse-slow {
              0%, 100% { transform: scale(1) translate(0px, 0px); opacity: 0.15; }
              50% { transform: scale(1.1) translate(5px, -5px); opacity: 0.3; }
            }
            @keyframes float-slow {
              0%, 100% { transform: translateY(0px) scale(1); opacity: 0.1; }
              50% { transform: translateY(-10px) scale(1.05); opacity: 0.25; }
            }
            .animate-pulse-slow {
              animation: pulse-slow 10s ease-in-out infinite;
            }
            .animate-float-slow {
              animation: float-slow 14s ease-in-out infinite;
            }
          `}</style>

          {/* Glowing background blobs */}
          <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-emerald-300/30 blur-3xl animate-pulse-slow" />
          <div
            className="pointer-events-none absolute -right-10 -bottom-20 h-60 w-60 rounded-full bg-white/10 blur-2xl animate-float-slow"
            style={{ animationDelay: '-4s' }}
          />
          <div
            className="pointer-events-none absolute right-[20%] top-[-10%] h-40 w-40 rounded-full bg-yellow-200/10 blur-3xl animate-pulse-slow"
            style={{ animationDelay: '-2s' }}
          />

          {/* Designer overlay pattern */}
          <img
            src="/images/dashboard-design.png"
            alt=""
            className="pointer-events-none absolute right-0 sm:right-24 md:right-48 top-0 h-full w-auto object-contain select-none opacity-95"
          />

          <div className="relative z-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-medium text-white/70">
                {greeting} · {today}
              </p>
              <h1 className="mt-1 text-2xl font-bold tracking-tight">
                {user?.name?.split(' ')[0] ?? 'Welcome'} 👋
              </h1>
              <p className="mt-1.5 text-sm text-white/70">
                {isSharjahOnly
                  ? "Here's your study request operations summary."
                  : "Here's your permit operations summary."}
              </p>
            </div>
            {can('permit:create') && (
              <Link
                to="/permits/new"
                className="flex items-center gap-2 self-start rounded-xl bg-white/20 border border-white/30 px-4 py-2.5 text-sm font-semibold text-white backdrop-blur-sm hover:bg-white/30 transition-all shrink-0"
              >
                <Plus size={16} /> New Permit
              </Link>
            )}
          </div>
        </div>

        {/* ── System stat cards ────────────────────────────────────────────── */}
        {canViewCustomers && (
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard
              icon={<Users size={18} />}
              iconBg="bg-blue-50"
              iconColor="text-blue-500"
              label="Total Customers"
              value={customerStats?.totalCustomers.value ?? 0}
              delta={customerStats?.totalCustomers.delta}
              loading={statsLoading}
            />
            <StatCard
              icon={<Building2 size={18} />}
              iconBg="bg-emerald-50"
              iconColor="text-emerald-500"
              label="Active Accounts"
              value={customerStats?.activeAccounts.value ?? 0}
              delta={customerStats?.activeAccounts.delta}
              loading={statsLoading}
            />
            <StatCard
              icon={<ShieldAlert size={18} />}
              iconBg="bg-amber-50"
              iconColor="text-amber-500"
              label="Pending Verification"
              value={customerStats?.pendingVerification.value ?? 0}
              delta={customerStats?.pendingVerification.delta}
              loading={statsLoading}
            />
            <StatCard
              icon={<BarChart3 size={18} />}
              iconBg="bg-violet-50"
              iconColor="text-violet-500"
              label="Total Permits"
              value={customerStats?.totalPermits.value ?? 0}
              delta={customerStats?.totalPermits.delta}
              loading={statsLoading}
            />
          </div>
        )}

        {/* ── Permit pipeline KPI row ──────────────────────────────────────── */}
        {canViewPermits && (
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard
              icon={<BadgeCheck size={18} />}
              iconBg="bg-emerald-50"
              iconColor="text-emerald-500"
              label="Issued This Month"
              value={dashboardStats?.permitsIssuedMTD ?? 0}
              loading={statsLoading2}
            />
            <StatCard
              icon={<Send size={18} />}
              iconBg="bg-blue-50"
              iconColor="text-blue-500"
              label="Submitted This Month"
              value={dashboardStats?.permitsSubmittedMTD ?? 0}
              loading={statsLoading2}
            />
            <StatCard
              icon={<TrendingUp size={18} />}
              iconBg="bg-brand-50"
              iconColor="text-brand-500"
              label="In Pipeline"
              value={inPipelineCount}
              loading={breakdownQuery.isLoading}
            />
            <StatCard
              icon={<TrendingDown size={18} />}
              iconBg="bg-red-50"
              iconColor="text-red-400"
              label="Rejection Rate"
              value={`${dashboardStats?.rejectionRate ?? 0}%`}
              loading={statsLoading2}
            />
          </div>
        )}

        {/* ── Study Request pipeline KPI row ───────────────────────────────── */}
        {canViewStudyRequests && (
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard
              icon={<BadgeCheck size={18} />}
              iconBg="bg-emerald-50"
              iconColor="text-emerald-500"
              label="Reports Issued This Month"
              value={studyStats?.issuedMTD ?? 0}
              loading={studyStatsLoading}
            />
            <StatCard
              icon={<Send size={18} />}
              iconBg="bg-blue-50"
              iconColor="text-blue-500"
              label="Submitted This Month"
              value={studyStats?.submittedMTD ?? 0}
              loading={studyStatsLoading}
            />
            <StatCard
              icon={<TrendingUp size={18} />}
              iconBg="bg-brand-50"
              iconColor="text-brand-500"
              label="In Pipeline"
              value={studyStats?.inPipelineCount ?? 0}
              loading={studyStatsLoading}
            />
            <StatCard
              icon={<TrendingDown size={18} />}
              iconBg="bg-red-50"
              iconColor="text-red-400"
              label="Rejection Rate"
              value={`${studyStats?.rejectionRate ?? 0}%`}
              loading={studyStatsLoading}
            />
          </div>
        )}

        {/* ── Action items (permission-based) ──────────────────────────────── */}
        {actionItems.length > 0 && (
          <div>
            <div className="mb-3 flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-brand-50">
                <Zap size={13} className="text-brand-500" />
              </div>
              <h2 className="text-sm font-semibold text-gray-900">Your Action Items</h2>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {actionItems.map((item) => (
                <ActionCard
                  key={item.key}
                  icon={item.icon}
                  iconBg={item.iconBg}
                  iconColor={item.iconColor}
                  label={item.label}
                  value={item.value}
                  description={item.description}
                  to={item.to}
                  search={item.search}
                  loading={item.loading}
                />
              ))}
            </div>
          </div>
        )}

        {/* ── Charts row 1: Pipeline funnel + Permit type donut ──────────── */}
        {canViewPermits && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
            {/* Pipeline funnel — horizontal bar */}
            <Card className="lg:col-span-3">
              <CardHeader>
                <CardTitle>Permit Pipeline</CardTitle>
                <CardDescription>Active permits by workflow stage</CardDescription>
                <CardAction>
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-brand-50">
                    <TrendingUp size={14} className="text-brand-500" />
                  </div>
                </CardAction>
              </CardHeader>
              <CardContent>
                {breakdownQuery.isLoading ? (
                  <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-5 w-full rounded-lg" />
                    ))}
                  </div>
                ) : !canViewPermits ? (
                  <div className="flex h-48 items-center justify-center text-sm text-gray-400">
                    No access to permit data.
                  </div>
                ) : (
                  <ChartContainer config={chartConfig} className="h-72 w-full">
                    <BarChart
                      data={breakdownQuery.data ?? []}
                      layout="vertical"
                      margin={{ top: 4, right: 32, bottom: 4, left: 4 }}
                      barCategoryGap="18%"
                      barSize={26}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="var(--color-border)"
                        horizontal={false}
                      />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 10, fill: 'var(--color-gray-400)' }}
                        tickLine={false}
                        axisLine={false}
                        allowDecimals={false}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        tick={{ fontSize: 10, fill: 'var(--color-gray-500)' }}
                        tickLine={false}
                        axisLine={false}
                        width={90}
                      />
                      <ChartTooltip
                        cursor={{ fill: 'var(--color-gray-50)' }}
                        content={<ChartTooltipContent hideLabel />}
                      />
                      <Bar
                        dataKey="count"
                        fill="var(--color-count)"
                        radius={[0, 6, 6, 0]}
                        isAnimationActive
                        animationDuration={700}
                        animationEasing="ease-out"
                      >
                        <LabelList
                          dataKey="count"
                          position="right"
                          style={{ fontSize: 10, fill: 'var(--color-gray-500)' }}
                        />
                      </Bar>
                    </BarChart>
                  </ChartContainer>
                )}
              </CardContent>
              <CardFooter>
                <TrendingUp size={14} className="text-brand-500 shrink-0" />
                <span className="ml-2 text-sm font-semibold text-gray-800">Active pipeline</span>
                <span className="ml-1.5 text-xs text-gray-400">
                  Permits by current workflow stage
                </span>
              </CardFooter>
            </Card>

            {/* Permits by type — donut */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Permits by Type</CardTitle>
                <CardDescription>ENV / Waste / Vehicle split</CardDescription>
                <CardAction>
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-violet-50">
                    <BarChart3 size={14} className="text-violet-500" />
                  </div>
                </CardAction>
              </CardHeader>
              <CardContent>
                {envCount.isLoading || wasteCount.isLoading || vehCount.isLoading ? (
                  <div className="flex h-48 items-center justify-center">
                    <Skeleton className="h-36 w-36 rounded-full" />
                  </div>
                ) : !canViewPermits || typeData.length === 0 ? (
                  <div className="flex h-48 items-center justify-center text-sm text-gray-400">
                    No permit data.
                  </div>
                ) : (
                  <ChartContainer config={donutChartConfig} className="h-72 w-full">
                    <PieChart>
                      <Pie
                        data={typeData}
                        dataKey="count"
                        nameKey="name"
                        innerRadius="58%"
                        outerRadius="90%"
                        paddingAngle={2}
                        isAnimationActive
                        animationDuration={700}
                        animationEasing="ease-out"
                      >
                        {typeData.map((d) => (
                          <Cell key={d.name} fill={TYPE_COLORS[d.name] ?? TYPE_COLORS.Other} />
                        ))}
                        <Label
                          content={({ viewBox }) => {
                            if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                              const { cx, cy } = viewBox;
                              const total = typeData.reduce((sum, d) => sum + d.count, 0);
                              return (
                                <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle">
                                  <tspan
                                    x={cx}
                                    y={cy}
                                    style={{
                                      fontSize: 32,
                                      fontWeight: 700,
                                      fill: 'var(--color-gray-900)',
                                    }}
                                  >
                                    {total}
                                  </tspan>
                                  <tspan
                                    x={cx}
                                    y={(cy ?? 0) + 24}
                                    style={{ fontSize: 13, fill: 'var(--color-gray-500)' }}
                                  >
                                    Total
                                  </tspan>
                                </text>
                              );
                            }
                          }}
                        />
                      </Pie>
                      <ChartTooltip content={<ChartTooltipContent hideLabel />} />
                      <ChartLegend content={<ChartLegendContent />} />
                    </PieChart>
                  </ChartContainer>
                )}
              </CardContent>
              <CardFooter>
                <BarChart3 size={14} className="text-violet-500 shrink-0" />
                <span className="ml-2 text-sm font-semibold text-gray-800">Permit mix</span>
                <span className="ml-1.5 text-xs text-gray-400">Total permits by type category</span>
              </CardFooter>
            </Card>
          </div>
        )}

        {/* ── Charts row 2: Monthly trend + Recent permits ─────────────────── */}
        {canViewPermits && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
            {/* Monthly submission trend */}
            <Card className="lg:col-span-3">
              <CardHeader>
                <CardTitle>Monthly Submissions</CardTitle>
                <CardDescription>Permits submitted over the last 12 months</CardDescription>
                <CardAction>
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-blue-50">
                    <TrendingUp size={14} className="text-blue-500" />
                  </div>
                </CardAction>
              </CardHeader>
              <CardContent>
                {statsLoading2 ? (
                  <div className="space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <Skeleton key={i} className="h-6 w-full rounded-lg" />
                    ))}
                  </div>
                ) : !canViewPermits || monthlyData.length === 0 ? (
                  <div className="flex h-48 items-center justify-center text-sm text-gray-400">
                    No trend data available.
                  </div>
                ) : (
                  <ChartContainer config={monthlyChartConfig} className="h-64 w-full">
                    <BarChart
                      data={monthlyData}
                      margin={{ top: 16, right: 4, bottom: 4, left: -20 }}
                      barCategoryGap="18%"
                      barSize={32}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="var(--color-border)"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="month"
                        tick={{ fontSize: 10, fill: 'var(--color-gray-400)' }}
                        tickLine={false}
                        axisLine={false}
                        interval={0}
                        angle={-28}
                        textAnchor="end"
                        height={48}
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: 'var(--color-gray-400)' }}
                        tickLine={false}
                        axisLine={false}
                        allowDecimals={false}
                      />
                      <ChartTooltip
                        cursor={{ fill: 'var(--color-gray-50)' }}
                        content={<ChartTooltipContent hideLabel />}
                      />
                      <Bar
                        dataKey="count"
                        fill="var(--color-count)"
                        radius={[6, 6, 0, 0]}
                        isAnimationActive
                        animationDuration={700}
                        animationEasing="ease-out"
                      >
                        <LabelList
                          dataKey="count"
                          position="top"
                          style={{ fontSize: 10, fill: 'var(--color-gray-500)' }}
                        />
                      </Bar>
                    </BarChart>
                  </ChartContainer>
                )}
              </CardContent>
              <CardFooter>
                <TrendingUp size={14} className="text-blue-500 shrink-0" />
                <span className="ml-2 text-sm font-semibold text-gray-800">Submission trend</span>
                <span className="ml-1.5 text-xs text-gray-400">
                  Showing permits submitted over the last 12 months
                </span>
              </CardFooter>
            </Card>

            {/* Recent permits */}
            <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Recent Permits</h3>
                  <p className="mt-0.5 text-xs text-gray-400">Latest submissions</p>
                </div>
                {canViewPermits && (
                  <Link
                    to="/permits"
                    search={{ page: 1, limit: 20 }}
                    className="flex items-center gap-1 text-xs font-semibold text-brand-500 hover:text-brand-600 transition-colors"
                  >
                    View all <ArrowRight size={12} />
                  </Link>
                )}
              </div>

              <div className="p-3">
                {recentQuery.isLoading ? (
                  <div className="space-y-2 p-2">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="flex items-center gap-3 p-2">
                        <Skeleton className="h-9 w-9 shrink-0 rounded-xl" />
                        <div className="flex-1 space-y-1.5">
                          <Skeleton className="h-3 w-3/4" />
                          <Skeleton className="h-2.5 w-1/2" />
                        </div>
                        <Skeleton className="h-5 w-16 rounded-full" />
                      </div>
                    ))}
                  </div>
                ) : !canViewPermits ? (
                  <p className="px-2 py-8 text-center text-sm text-gray-400">
                    No access to permit data.
                  </p>
                ) : !recentQuery.data?.length ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <CheckCircle2 size={32} className="text-gray-200" />
                    <p className="mt-2 text-sm text-gray-400">No permits yet</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {recentQuery.data.map((permit) => {
                      const typeCode = permit.requestTypeConfig?.code ?? '';
                      return (
                        <Link
                          key={permit.id}
                          to="/permits/$permitId"
                          params={{ permitId: permit.id }}
                          className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-gray-50 transition-colors group"
                        >
                          <div
                            className={cn(
                              'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[10px] font-bold',
                              PERMIT_TYPE_COLOR[typeCode] ?? 'bg-gray-100 text-gray-500',
                            )}
                          >
                            {typeCode || 'PRM'}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-semibold text-gray-800 font-mono group-hover:text-brand-600 transition-colors">
                              {permit.referenceNo}
                            </p>
                            <p className="truncate text-[10px] text-gray-400 mt-0.5">
                              {permit.account?.name ?? '—'}
                            </p>
                          </div>
                          <Badge
                            variant={STATUS_BADGE[permit.status] ?? 'default'}
                            className="shrink-0 text-[10px]"
                          >
                            {STATUS_LABEL[permit.status] ?? permit.status}
                          </Badge>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Study Request charts: Pipeline funnel + Recent study requests ─── */}
        {canViewStudyRequests && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
            {/* Study Request pipeline funnel — horizontal bar */}
            <Card className="lg:col-span-3">
              <CardHeader>
                <CardTitle>Study Request Pipeline</CardTitle>
                <CardDescription>Active study requests by workflow stage</CardDescription>
                <CardAction>
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-brand-50">
                    <TrendingUp size={14} className="text-brand-500" />
                  </div>
                </CardAction>
              </CardHeader>
              <CardContent>
                {studyStatsLoading ? (
                  <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-5 w-full rounded-lg" />
                    ))}
                  </div>
                ) : studyBreakdownData.length === 0 ? (
                  <div className="flex h-48 items-center justify-center text-sm text-gray-400">
                    No study request data.
                  </div>
                ) : (
                  <ChartContainer config={studyChartConfig} className="h-72 w-full">
                    <BarChart
                      data={studyBreakdownData}
                      layout="vertical"
                      margin={{ top: 4, right: 32, bottom: 4, left: 4 }}
                      barCategoryGap="18%"
                      barSize={26}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="var(--color-border)"
                        horizontal={false}
                      />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 10, fill: 'var(--color-gray-400)' }}
                        tickLine={false}
                        axisLine={false}
                        allowDecimals={false}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        tick={{ fontSize: 10, fill: 'var(--color-gray-500)' }}
                        tickLine={false}
                        axisLine={false}
                        width={90}
                      />
                      <ChartTooltip
                        cursor={{ fill: 'var(--color-gray-50)' }}
                        content={<ChartTooltipContent hideLabel />}
                      />
                      <Bar
                        dataKey="count"
                        fill="var(--color-count)"
                        radius={[0, 6, 6, 0]}
                        isAnimationActive
                        animationDuration={700}
                        animationEasing="ease-out"
                      >
                        <LabelList
                          dataKey="count"
                          position="right"
                          style={{ fontSize: 10, fill: 'var(--color-gray-500)' }}
                        />
                      </Bar>
                    </BarChart>
                  </ChartContainer>
                )}
              </CardContent>
              <CardFooter>
                <TrendingUp size={14} className="text-brand-500 shrink-0" />
                <span className="ml-2 text-sm font-semibold text-gray-800">Active pipeline</span>
                <span className="ml-1.5 text-xs text-gray-400">
                  Study requests by current workflow stage
                </span>
              </CardFooter>
            </Card>

            {/* Recent study requests */}
            <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Recent Study Requests</h3>
                  <p className="mt-0.5 text-xs text-gray-400">Latest submissions</p>
                </div>
                <Link
                  to="/study-requests"
                  search={{ page: 1, limit: 20 }}
                  className="flex items-center gap-1 text-xs font-semibold text-brand-500 hover:text-brand-600 transition-colors"
                >
                  View all <ArrowRight size={12} />
                </Link>
              </div>

              <div className="p-3">
                {recentStudyQuery.isLoading ? (
                  <div className="space-y-2 p-2">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="flex items-center gap-3 p-2">
                        <Skeleton className="h-9 w-9 shrink-0 rounded-xl" />
                        <div className="flex-1 space-y-1.5">
                          <Skeleton className="h-3 w-3/4" />
                          <Skeleton className="h-2.5 w-1/2" />
                        </div>
                        <Skeleton className="h-5 w-16 rounded-full" />
                      </div>
                    ))}
                  </div>
                ) : !recentStudyQuery.data?.items.length ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <CheckCircle2 size={32} className="text-gray-200" />
                    <p className="mt-2 text-sm text-gray-400">No study requests yet</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {recentStudyQuery.data.items.map((sr) => (
                      <Link
                        key={sr.id}
                        to="/study-requests/$requestId"
                        params={{ requestId: sr.id }}
                        className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-gray-50 transition-colors group"
                      >
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-[10px] font-bold text-gray-500">
                          SM
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs font-semibold text-gray-800 font-mono group-hover:text-brand-600 transition-colors">
                            {sr.referenceNo}
                          </p>
                          <p className="truncate text-[10px] text-gray-400 mt-0.5">
                            {sr.account?.name ?? '—'}
                          </p>
                        </div>
                        <Badge
                          variant={STUDY_STATUS_BADGE[sr.status] ?? 'default'}
                          className="shrink-0 text-[10px]"
                        >
                          {STUDY_STATUS_LABEL[sr.status] ?? sr.status}
                        </Badge>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Study Request charts: Monthly trend ──────────────────────────── */}
        {canViewStudyRequests && (
          <Card>
            <CardHeader>
              <CardTitle>Monthly Study Request Submissions</CardTitle>
              <CardDescription>Study requests submitted over the last 12 months</CardDescription>
              <CardAction>
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-blue-50">
                  <TrendingUp size={14} className="text-blue-500" />
                </div>
              </CardAction>
            </CardHeader>
            <CardContent>
              {studyStatsLoading ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-6 w-full rounded-lg" />
                  ))}
                </div>
              ) : studyMonthlyData.length === 0 ? (
                <div className="flex h-48 items-center justify-center text-sm text-gray-400">
                  No trend data available.
                </div>
              ) : (
                <ChartContainer config={studyMonthlyChartConfig} className="h-64 w-full">
                  <BarChart
                    data={studyMonthlyData}
                    margin={{ top: 16, right: 4, bottom: 4, left: -20 }}
                    barCategoryGap="18%"
                    barSize={32}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="var(--color-border)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 10, fill: 'var(--color-gray-400)' }}
                      tickLine={false}
                      axisLine={false}
                      interval={0}
                      angle={-28}
                      textAnchor="end"
                      height={48}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: 'var(--color-gray-400)' }}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                    />
                    <ChartTooltip
                      cursor={{ fill: 'var(--color-gray-50)' }}
                      content={<ChartTooltipContent hideLabel />}
                    />
                    <Bar
                      dataKey="count"
                      fill="var(--color-count)"
                      radius={[6, 6, 0, 0]}
                      isAnimationActive
                      animationDuration={700}
                      animationEasing="ease-out"
                    >
                      <LabelList
                        dataKey="count"
                        position="top"
                        style={{ fontSize: 10, fill: 'var(--color-gray-500)' }}
                      />
                    </Bar>
                  </BarChart>
                </ChartContainer>
              )}
            </CardContent>
            <CardFooter>
              <TrendingUp size={14} className="text-blue-500 shrink-0" />
              <span className="ml-2 text-sm font-semibold text-gray-800">Submission trend</span>
              <span className="ml-1.5 text-xs text-gray-400">
                Showing study requests submitted over the last 12 months
              </span>
            </CardFooter>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}
